import React, { useState } from 'react';
import { HelpCircle, X } from 'lucide-react';

/**
 * HelpTooltip - Context-sensitive help tooltip component
 * 
 * Usage:
 * <HelpTooltip content="This is helpful information" />
 * <HelpTooltip title="Feature Name" content="Detailed explanation..." position="right" />
 */
const HelpTooltip = ({ 
  title, 
  content, 
  position = 'top',
  size = 'md',
  className = '' 
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2'
  };

  const arrowClasses = {
    top: 'top-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent border-t-gray-900',
    bottom: 'bottom-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-t-transparent border-b-gray-900',
    left: 'left-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent border-l-gray-900',
    right: 'right-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent border-r-gray-900'
  };

  return (
    <div className={`relative inline-block ${className}`}>
      {/* Help Icon */}
      <button
        type="button"
        className="text-gray-400 hover:text-blue-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-full"
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Help"
      >
        <HelpCircle className={sizeClasses[size]} />
      </button>

      {/* Tooltip */}
      {isOpen && (
        <div 
          className={`absolute z-50 ${positionClasses[position]} animate-in fade-in duration-200`}
          style={{ minWidth: '200px', maxWidth: '320px' }}
        >
          {/* Arrow */}
          <div 
            className={`absolute w-0 h-0 border-8 ${arrowClasses[position]}`}
          />
          
          {/* Content */}
          <div className="bg-gray-900 text-white rounded-lg shadow-xl p-4">
            {title && (
              <div className="flex items-start justify-between mb-2">
                <h4 className="font-semibold text-sm">{title}</h4>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-white ml-2 -mt-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            <div className="text-sm text-gray-200 leading-relaxed">
              {typeof content === 'string' ? (
                <p>{content}</p>
              ) : (
                content
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HelpTooltip;

